/**
 * AI-Automated Hospice Medication Workflow Dashboard
 * 
 * Main dashboard for the medication workflow system:
 * - Physician Order Queue (one-click approval)
 * - MAR Summary View
 * - Pharmacy Status Tracking (MOCKED)
 * - Operations Metrics
 * 
 * Key Features:
 * - Red/Yellow/Green clinical confidence indicators
 * - Target: ≤30 second physician review time
 * - AI-generated justification language
 * - Audit-ready documentation
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Pill, CheckCircle, XCircle, Clock, AlertTriangle,
  User, RefreshCw, Send, FileText, TrendingUp,
  Package, Truck, Timer, Shield, Activity,
  AlertCircle, ChevronRight, Brain, Sparkles,
  ClipboardCheck, Stethoscope, Heart, FileCheck, Mic
} from "lucide-react";
import VoiceMedCommand from "@/components/VoiceMedCommand";

const API = process.env.REACT_APP_BACKEND_URL;

// Confidence level colors
const CONFIDENCE_COLORS = {
  high: { bg: "bg-green-100", text: "text-green-800", border: "border-green-500", icon: "text-green-600" },
  medium: { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-500", icon: "text-amber-600" },
  low: { bg: "bg-red-100", text: "text-red-800", border: "border-red-500", icon: "text-red-600" }
};

// Delivery priority badges
const PRIORITY_BADGES = {
  stat: { label: "STAT", className: "bg-red-600 text-white" },
  same_day: { label: "Same Day", className: "bg-amber-500 text-white" },
  routine: { label: "Routine", className: "bg-blue-500 text-white" }
};

export default function MedicationWorkflowDashboard({ userRole = "physician" }) {
  const [activeTab, setActiveTab] = useState("physician");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Dashboard data
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [physicianQueue, setPhysicianQueue] = useState([]);
  const [pharmacyDashboard, setPharmacyDashboard] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [showBundleDialog, setShowBundleDialog] = useState(false);
  
  // Approval state
  const [approving, setApproving] = useState(false);
  const [selectedRecs, setSelectedRecs] = useState([]);
  
  // Trigger test data
  const [triggerPatientId, setTriggerPatientId] = useState("demo-patient-001");
  const [triggerLoading, setTriggerLoading] = useState(false);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      const [summaryRes, queueRes, pharmacyRes] = await Promise.all([
        fetch(`${API}/api/medication-workflow/dashboard/summary`),
        fetch(`${API}/api/medication-workflow/physician/queue`),
        fetch(`${API}/api/pharmacy/dashboard`)
      ]);
      
      if (summaryRes.ok) {
        const summary = await summaryRes.json();
        setDashboardSummary(summary);
      }
      
      if (queueRes.ok) {
        const queue = await queueRes.json();
        setPhysicianQueue(queue.queue || []);
      }
      
      if (pharmacyRes.ok) {
        const pharmacy = await pharmacyRes.json();
        setPharmacyDashboard(pharmacy);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  // View bundle details
  const handleViewBundle = (bundle) => {
    setSelectedBundle(bundle);
    setSelectedRecs(bundle.recommendations?.map(r => r.id) || []);
    setShowBundleDialog(true);
  };

  // Toggle recommendation selection
  const toggleRecSelection = (recId) => {
    setSelectedRecs(prev => 
      prev.includes(recId) 
        ? prev.filter(id => id !== recId)
        : [...prev, recId]
    );
  };

  // Bulk approve bundle
  const handleBulkApprove = async () => {
    if (!selectedBundle || selectedRecs.length === 0) return;
    
    setApproving(true);
    try {
      const response = await fetch(`${API}/api/medication-workflow/physician/bulk-approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bundle_id: selectedBundle.id,
          approved_recommendation_ids: selectedRecs,
          rejected_recommendation_ids: selectedBundle.recommendations
            ?.filter(r => !selectedRecs.includes(r.id))
            .map(r => r.id) || []
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Approved ${result.approved_count} medications`);
        setShowBundleDialog(false);
        fetchDashboardData();
      } else {
        toast.error("Failed to approve medications");
      }
    } catch (error) {
      console.error("Approval error:", error);
      toast.error("Error during approval");
    } finally {
      setApproving(false);
    }
  };

  // Voice command: Approve all in bundle
  const handleVoiceApproveAll = async (bundleId) => {
    const bundle = physicianQueue.find(b => b.id === bundleId);
    if (!bundle) return;
    
    try {
      const allRecIds = bundle.recommendations?.map(r => r.id) || [];
      const response = await fetch(`${API}/api/medication-workflow/physician/bulk-approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bundle_id: bundleId,
          approved_recommendation_ids: allRecIds,
          rejected_recommendation_ids: []
        })
      });
      
      if (response.ok) {
        fetchDashboardData();
        return true;
      }
    } catch (error) {
      console.error("Voice approve all error:", error);
    }
    return false;
  };

  // Voice command: Approve single medication
  const handleVoiceApproveMed = async (recId, bundleId) => {
    try {
      const response = await fetch(`${API}/api/medication-workflow/physician/bulk-approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bundle_id: bundleId,
          approved_recommendation_ids: [recId],
          rejected_recommendation_ids: []
        })
      });
      
      if (response.ok) {
        fetchDashboardData();
        return true;
      }
    } catch (error) {
      console.error("Voice approve med error:", error);
    }
    return false;
  };

  // Voice command: Reject single medication
  const handleVoiceRejectMed = async (recId, bundleId) => {
    try {
      const response = await fetch(`${API}/api/medication-workflow/physician/bulk-approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bundle_id: bundleId,
          approved_recommendation_ids: [],
          rejected_recommendation_ids: [recId]
        })
      });
      
      if (response.ok) {
        fetchDashboardData();
        return true;
      }
    } catch (error) {
      console.error("Voice reject med error:", error);
    }
    return false;
  };

  // Test admission trigger
  const handleTestAdmissionTrigger = async () => {
    setTriggerLoading(true);
    try {
      const response = await fetch(
        `${API}/api/medication-workflow/triggers/admission?patient_id=${triggerPatientId}&admission_id=adm-${Date.now()}&primary_diagnosis=Terminal%20Cancer&pps_score=50`,
        { method: "POST" }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Generated ${result.recommendations_count} recommendations`);
        fetchDashboardData();
      } else {
        toast.error("Failed to trigger workflow");
      }
    } catch (error) {
      console.error("Trigger error:", error);
      toast.error("Error triggering workflow");
    } finally {
      setTriggerLoading(false);
    }
  };

  // Send to pharmacy
  const handleSendToPharmacy = async (orderId, priority = "routine") => {
    try {
      const response = await fetch(
        `${API}/api/medication-workflow/orders/${orderId}/send-to-pharmacy?delivery_priority=${priority}`,
        { method: "POST" }
      );
      
      if (response.ok) {
        toast.success("Order sent to pharmacy");
        fetchDashboardData();
      } else {
        toast.error("Failed to send to pharmacy");
      }
    } catch (error) {
      toast.error("Error sending to pharmacy");
    }
  };

  const getConfidenceColor = (level) => CONFIDENCE_COLORS[level] || CONFIDENCE_COLORS.medium;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Pill className="h-6 w-6 text-purple-600" />
            AI Medication Workflow
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Automated medication recommendations with one-click physician approval
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-100">
                <ClipboardCheck className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending Review</p>
                <p className="text-2xl font-bold">
                  {dashboardSummary?.recommendations?.pending_review || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Approved Today</p>
                <p className="text-2xl font-bold">
                  {dashboardSummary?.orders?.today || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-100">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">At Pharmacy</p>
                <p className="text-2xl font-bold">
                  {dashboardSummary?.orders?.sent_to_pharmacy || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-amber-100">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Alerts</p>
                <p className="text-2xl font-bold">
                  {dashboardSummary?.alerts?.unresolved || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="physician" className="flex items-center gap-2">
            <Stethoscope className="h-4 w-4" />
            Physician Queue
            {physicianQueue.length > 0 && (
              <Badge variant="destructive" className="ml-1">{physicianQueue.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pharmacy" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Pharmacy
          </TabsTrigger>
          <TabsTrigger value="operations" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Operations
          </TabsTrigger>
          <TabsTrigger value="test" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Test Triggers
          </TabsTrigger>
        </TabsList>

        {/* Physician Queue Tab */}
        <TabsContent value="physician" className="space-y-4">
          {/* Voice Command Interface */}
          <VoiceMedCommand
            physicianQueue={physicianQueue}
            selectedBundle={selectedBundle}
            onApproveAll={handleVoiceApproveAll}
            onApproveMed={handleVoiceApproveMed}
            onRejectMed={handleVoiceRejectMed}
            onRefresh={fetchDashboardData}
            onSelectBundle={(bundle) => {
              setSelectedBundle(bundle);
              setSelectedRecs(bundle.recommendations?.map(r => r.id) || []);
            }}
          />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600" />
                Orders Ready for Review
              </CardTitle>
              <CardDescription>
                One-click approval • Voice commands enabled • Target: ≤30 seconds per bundle
              </CardDescription>
            </CardHeader>
            <CardContent>
              {physicianQueue.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p className="text-lg font-medium">All caught up!</p>
                  <p className="text-sm">No pending medication orders to review</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {physicianQueue.map((bundle) => {
                    const confidence = getConfidenceColor(bundle.confidence_indicator);
                    return (
                      <Card 
                        key={bundle.id} 
                        className={`cursor-pointer hover:shadow-md transition-shadow ${confidence.border} border-l-4`}
                        onClick={() => handleViewBundle(bundle)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-lg ${confidence.bg}`}>
                                <Brain className={`h-5 w-5 ${confidence.icon}`} />
                              </div>
                              <div>
                                <p className="font-medium">{bundle.patient_name}</p>
                                <p className="text-sm text-gray-500">
                                  {bundle.trigger_type?.replace(/_/g, ' ')} • {bundle.recommendations?.length || 0} medications
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <Badge className={confidence.bg + ' ' + confidence.text}>
                                  {bundle.confidence_indicator?.toUpperCase() || 'MEDIUM'} Confidence
                                </Badge>
                                <p className="text-xs text-gray-400 mt-1">
                                  ~{bundle.estimated_review_time_seconds || 30}s review time
                                </p>
                              </div>
                              <ChevronRight className="h-5 w-5 text-gray-400" />
                            </div>
                          </div>
                          
                          {/* Quick preview of medications */}
                          <div className="mt-3 flex flex-wrap gap-2">
                            {bundle.recommendations?.slice(0, 5).map((rec) => (
                              <Badge key={rec.id} variant="outline" className="text-xs">
                                {rec.medication_name}
                                {rec.controlled_substance && (
                                  <Shield className="h-3 w-3 ml-1 text-amber-600" />
                                )}
                              </Badge>
                            ))}
                            {(bundle.recommendations?.length || 0) > 5 && (
                              <Badge variant="outline" className="text-xs">
                                +{bundle.recommendations.length - 5} more
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pharmacy Tab */}
        <TabsContent value="pharmacy" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Pharmacy Dashboard
                <Badge variant="outline" className="ml-2">MOCKED</Badge>
              </CardTitle>
              <CardDescription>
                Order tracking • Delivery SLA monitoring
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pharmacyDashboard ? (
                <div className="space-y-6">
                  {/* Priority Queue */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="border-l-4 border-red-500">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">STAT Orders</p>
                            <p className="text-2xl font-bold">{pharmacyDashboard.priority_queue?.stat || 0}</p>
                          </div>
                          <Timer className="h-8 w-8 text-red-500" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-amber-500">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Same Day</p>
                            <p className="text-2xl font-bold">{pharmacyDashboard.priority_queue?.same_day || 0}</p>
                          </div>
                          <Clock className="h-8 w-8 text-amber-500" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-green-500">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Delivered</p>
                            <p className="text-2xl font-bold">{pharmacyDashboard.summary?.delivered || 0}</p>
                          </div>
                          <CheckCircle className="h-8 w-8 text-green-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-gray-500">Avg Fill Time</p>
                        <p className="text-xl font-bold">
                          {pharmacyDashboard.metrics?.avg_fill_time_hours || 0} hours
                        </p>
                        <Progress value={75} className="mt-2" />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-gray-500">On-Time Delivery</p>
                        <p className="text-xl font-bold">
                          {((pharmacyDashboard.metrics?.on_time_delivery_rate || 0) * 100).toFixed(0)}%
                        </p>
                        <Progress 
                          value={(pharmacyDashboard.metrics?.on_time_delivery_rate || 0) * 100} 
                          className="mt-2" 
                        />
                      </CardContent>
                    </Card>
                  </div>

                  {/* Status Breakdown */}
                  <div className="grid grid-cols-5 gap-2">
                    {['pending', 'processing', 'filled', 'shipped', 'delivered'].map((status) => (
                      <div key={status} className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 capitalize">{status}</p>
                        <p className="text-lg font-bold">
                          {pharmacyDashboard.summary?.[status] || 0}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Loading pharmacy data...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-green-600" />
                Operations Dashboard
              </CardTitle>
              <CardDescription>
                Workflow metrics • SLA tracking • Compliance monitoring
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <Timer className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                    <p className="text-sm text-gray-500">Avg Review Time</p>
                    <p className="text-xl font-bold">
                      {dashboardSummary?.metrics?.avg_physician_review_seconds || 25}s
                    </p>
                    <Badge className={
                      (dashboardSummary?.metrics?.avg_physician_review_seconds || 25) <= 30 
                        ? "bg-green-100 text-green-800" 
                        : "bg-amber-100 text-amber-800"
                    }>
                      {(dashboardSummary?.metrics?.avg_physician_review_seconds || 25) <= 30 ? "On Target" : "Above Target"}
                    </Badge>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4 text-center">
                    <Truck className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                    <p className="text-sm text-gray-500">Pharmacy Turnaround</p>
                    <p className="text-xl font-bold">
                      {dashboardSummary?.metrics?.avg_pharmacy_turnaround_hours || 4.5}h
                    </p>
                    <Badge className="bg-green-100 text-green-800">On Track</Badge>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4 text-center">
                    <Shield className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                    <p className="text-sm text-gray-500">Compliance Rate</p>
                    <p className="text-xl font-bold">98%</p>
                    <Badge className="bg-green-100 text-green-800">Excellent</Badge>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4 text-center">
                    <TrendingUp className="h-8 w-8 mx-auto mb-2 text-green-500" />
                    <p className="text-sm text-gray-500">Orders Today</p>
                    <p className="text-xl font-bold">
                      {dashboardSummary?.orders?.today || 0}
                    </p>
                    <Badge variant="outline">All Time: {dashboardSummary?.orders?.total || 0}</Badge>
                  </CardContent>
                </Card>
              </div>

              {/* Alerts Summary */}
              {(dashboardSummary?.alerts?.unresolved || 0) > 0 && (
                <Card className="mt-4 border-amber-500 border-l-4">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-amber-600">
                      <AlertTriangle className="h-5 w-5" />
                      <p className="font-medium">
                        {dashboardSummary.alerts.unresolved} unresolved alerts
                        {dashboardSummary.alerts.critical > 0 && (
                          <span className="text-red-600 ml-2">
                            ({dashboardSummary.alerts.critical} critical)
                          </span>
                        )}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Test Triggers Tab */}
        <TabsContent value="test" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Test Medication Workflow Triggers
              </CardTitle>
              <CardDescription>
                Simulate admission and visit events to generate AI recommendations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Admission Trigger</CardTitle>
                    <CardDescription>
                      Simulates new patient admission with comfort kit + symptom-based meds
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Patient ID</label>
                      <Input 
                        value={triggerPatientId}
                        onChange={(e) => setTriggerPatientId(e.target.value)}
                        placeholder="demo-patient-001"
                      />
                    </div>
                    <Button 
                      onClick={handleTestAdmissionTrigger}
                      disabled={triggerLoading}
                      className="w-full"
                    >
                      {triggerLoading ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Brain className="h-4 w-4 mr-2" />
                      )}
                      Trigger Admission Workflow
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Workflow Info</CardTitle>
                    <CardDescription>
                      How the AI medication workflow operates
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                        <p>Event triggers workflow (admission, visit, PRN usage)</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Brain className="h-4 w-4 text-purple-500 mt-0.5" />
                        <p>AI generates medication recommendations</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Stethoscope className="h-4 w-4 text-blue-500 mt-0.5" />
                        <p>Physician reviews and approves (one-click)</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Package className="h-4 w-4 text-amber-500 mt-0.5" />
                        <p>Auto-push to pharmacy (MOCKED)</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <FileCheck className="h-4 w-4 text-green-500 mt-0.5" />
                        <p>MAR updated automatically</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bundle Review Dialog */}
      <Dialog open={showBundleDialog} onOpenChange={setShowBundleDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Medication Order Review
            </DialogTitle>
            <DialogDescription>
              {selectedBundle?.patient_name} • {selectedBundle?.trigger_type?.replace(/_/g, ' ')}
            </DialogDescription>
          </DialogHeader>

          {selectedBundle && (
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4">
                {/* Clinical Summary */}
                <Card className="bg-gray-50">
                  <CardContent className="pt-4">
                    <pre className="text-sm whitespace-pre-wrap font-mono">
                      {selectedBundle.clinical_summary}
                    </pre>
                  </CardContent>
                </Card>

                {/* Recommendations */}
                <div className="space-y-3">
                  {selectedBundle.recommendations?.map((rec) => {
                    const isSelected = selectedRecs.includes(rec.id);
                    const confidence = getConfidenceColor(rec.confidence_level);
                    
                    return (
                      <Card 
                        key={rec.id}
                        className={`cursor-pointer transition-all ${
                          isSelected 
                            ? 'ring-2 ring-green-500 bg-green-50/50' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => toggleRecSelection(rec.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected 
                                  ? 'bg-green-500 border-green-500' 
                                  : 'border-gray-300'
                              }`}>
                                {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{rec.medication_name}</p>
                                  {rec.controlled_substance && (
                                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                                      <Shield className="h-3 w-3 mr-1" />
                                      C-{rec.controlled_substance_schedule}
                                    </Badge>
                                  )}
                                  <Badge className={`${confidence.bg} ${confidence.text}`}>
                                    {rec.confidence_level}
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  {rec.dose} {rec.dose_unit} • {rec.route} • {rec.frequency}
                                </p>
                                <p className="text-xs text-gray-500 mt-2">
                                  {rec.ai_rationale}
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {rec.recommendation_type}
                            </Badge>
                          </div>

                          {/* Drug interactions */}
                          {rec.drug_interactions?.length > 0 && (
                            <div className="mt-3 p-2 bg-amber-50 rounded-lg">
                              <p className="text-xs font-medium text-amber-800 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Drug Interactions
                              </p>
                              {rec.drug_interactions.map((int, idx) => (
                                <p key={idx} className="text-xs text-amber-700 mt-1">
                                  • {int.description}
                                </p>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Auto-written phrasing */}
                {selectedBundle.auto_written_phrasing && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4">
                      <p className="text-sm font-medium text-blue-800 mb-2">
                        Standard Hospice Phrasing (Auto-generated)
                      </p>
                      <p className="text-sm text-blue-700">
                        {selectedBundle.auto_written_phrasing}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <p className="text-sm text-gray-500">
                {selectedRecs.length} of {selectedBundle?.recommendations?.length || 0} selected
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBundleDialog(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleBulkApprove}
                  disabled={approving || selectedRecs.length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {approving ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Approve & Sign ({selectedRecs.length})
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
